import {
  EnvironmentInjector,
  Injector,
  Service,
  createEnvironmentInjector,
} from '@angular/core';
import { FieldEntry, TaskFieldSet, createFieldEntry } from './field-entry';
import { FieldDefinition } from './field.model';
import { FieldShape } from './field-rules';

interface FieldKind {
  initialValue: (value: unknown) => unknown;
  shape: FieldShape;
}

const TEXT_TYPES = new Set(['text', 'text-input', 'textarea', 'remarks']);

const DEFAULT_KIND: FieldKind = {
  initialValue: value => value ?? null,
  shape: 'scalar',
};

@Service()
export class ControlFactory {
  private readonly kinds = new Map<string, FieldKind>();

  register(
    type: string,
    initialValue: (value: unknown) => unknown,
    shape: FieldShape = 'scalar'
  ): void {
    this.kinds.set(type, { initialValue, shape });
  }

  initialValueFor(type: string, value: unknown): unknown {
    return (this.kinds.get(type) ?? DEFAULT_KIND).initialValue(value);
  }

  create(f: FieldDefinition, injector: Injector): FieldEntry {
    const kind = this.kinds.get(f.type) ?? DEFAULT_KIND;
    return createFieldEntry(kind.initialValue(f.value), injector, kind.shape);
  }

  createSet(
    fields: readonly FieldDefinition[],
    parent: EnvironmentInjector
  ): TaskFieldSet {
    const owner = createEnvironmentInjector([], parent);
    const byId = new Map<string, FieldEntry>();
    for (const f of fields) byId.set(f.id, this.create(f, owner));
    return new TaskFieldSet(owner, byId);
  }

  // Text controls bind to native inputs, whose [formField] type is string.
  coerce(type: string, value: unknown): unknown {
    return TEXT_TYPES.has(type) ? (value ?? '') : value;
  }
}
